// @flow
import React from "react";
import withStyles from "elevate-ui/withStyles";
import classNames from "classnames";
import Typography from "elevate-ui/Typography";
import { withGlobalContext } from "../../common/GlobalContext";

type $Props = {
  BATHS_OPTIONS: Array<Object>,
  BEDS_OPTIONS: Array<Object>,
  PRICE_OPTIONS: Array<Object>,
  classes: Object,
  filterBackground?: string,
  filterPosition?: string,
  filtersOpen: boolean,
  handleSelectChange: Function,
  handlePropertyTypes: Function,
  initialFilterState?: string,
  compact?: boolean,
};

const Filters = ({
  BATHS_OPTIONS,
  BEDS_OPTIONS,
  PRICE_OPTIONS,
  classes,
  filtersOpen,
  handleSelectChange,
  handlePropertyTypes,
}: $Props) => (
  <div
    className={classNames({
      [classes.root]: true,
      [classes.active]: filtersOpen,
    })}
  >
    <div className={classes.propertyTypes}>
      <Typography type="body" style={{ fontWeight: "bold" }}>
        Property Types
      </Typography>
      <div className={classes.propertyTypeSelections}>
        <div>
          <input
            onChange={handlePropertyTypes}
            type="checkbox"
            id="singleFamily"
            value="Single Family"
          />
          <label htmlFor="singleFamily">Single Family</label>
        </div>
        <div>
          <input
            onChange={handlePropertyTypes}
            type="checkbox"
            id="condo"
            value="Condo"
          />
          <label htmlFor="condo">Condo</label>
        </div>
        <div>
          <input
            onChange={handlePropertyTypes}
            type="checkbox"
            id="townhouse"
            value="Townhouse"
          />
          <label htmlFor="townhouse">Townhouse</label>
        </div>
        <div>
          <input
            onChange={handlePropertyTypes}
            type="checkbox"
            id="multiFamily"
            value="Multifamily"
          />
          <label htmlFor="multiFamily">Multi Family</label>
        </div>
      </div>
    </div>
    <div className={classes.miscFilterContainer}>
      <div>
        <label>Min Price</label>
        <select id="minPrice" name="minPrice" onChange={handleSelectChange}>
          {PRICE_OPTIONS.map((price) => (
            <option key={price.key} value={price.key}>
              {price.value}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label>Max Price</label>
        <select id="maxPrice" name="maxPrice" onChange={handleSelectChange}>
          {PRICE_OPTIONS.map((price) => (
            <option key={price.key} value={price.key}>
              {price.value}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label>Min Beds</label>
        <select id="minBeds" name="minBeds" onChange={handleSelectChange}>
          {BEDS_OPTIONS.map((bed) => (
            <option key={bed.key} value={bed.key}>
              {bed.value}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label>Min Baths</label>
        <select id="minBaths" name="minBaths" onChange={handleSelectChange}>
          {BATHS_OPTIONS.map((bath) => (
            <option key={bath.key} value={bath.key}>
              {bath.value}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

const styles = (theme) => ({
  root: {
    display: (props) =>
      props.initialFilterState === "open" ? "block" : "none",
    zIndex: theme.zIndex.dropdown,
    [theme.breakpoints(900)]: {
      position: (props) => props.filterPosition || "absolute",
      left: "0",
      width: "100%",
      maxHeight: "0",
      padding: (props) => (props.compact ? "0px" : "0px 24px"),
      overflow: "hidden",
      backgroundColor: (props) =>
        props.filterBackground || theme.colors.primary["500"],
      transition: theme.transitions.default,
    },
  },
  active: {
    display: "block !important",
    maxHeight: "900px",
    padding: (props) => (props.compact ? "0px" : "0 24px 24px"),
  },
  propertyTypes: {
    margin: "24px auto 12px",
    padding: "8px 16px",
    borderRadius: theme.globalBorderRadius,
    backgroundColor: theme.colors.white,
  },
  propertyTypeSelections: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "space-between",
    alignItems: "center",
    "& div": {
      flexBasis: (props) => (props.compact ? "50%" : null),
    },
    "& input[type=checkbox]": {
      position: "relative",
      width: "24px",
      height: "24px",
      margin: "0",
      appearance: "none",
      "-webkit-appearance": "none",
      "-moz-appearance": "none",
      verticalAlign: "middle",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%233E464F'%3E%3Cpath d='M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z'%3E%3C/path%3E%3C/svg%3E")`,
    },
    "& input[type=checkbox]:checked": {
      backgroundImage: `url("data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%233E464F'%3E%3Cpath d='M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /%3E%3C/svg%3E")`,
    },
    "& label": {
      margin: "0 0 0 6px",
      fontWeight: "400",
    },
  },
  miscFilterContainer: {
    display: "grid",
    position: "relative",
    gridTemplateRows: "1fr 1fr",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: "12px",
    gridRowGap: "12px",
    backgroundColor: theme.colors.white,
    borderRadius: theme.globalBorderRadius,
    padding: "8px 16px",
    "& select": {
      display: "block",
      position: "relative",
      width: "100%",
      padding: theme.spacing.unit * 0.5,
      border: theme.borders["default"],
      color: theme.colors.gray["900"],
      borderRadius: "6px",
      background: `${
        theme.colors.white
      } url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 1792 1792'%3E%3Cpath fill='%23ccd2d8' d='M1395 736q0 13-10 23l-466 466q-10 10-23 10t-23-10l-466-466q-10-10-10-23t10-23l50-50q10-10 23-10t23 10l393 393 393-393q10-10 23-10t23 10l50 50q10 10 10 23z'%3E%3C/path%3E%3C/svg%3E") right center/24px no-repeat`,
      appearance: "none",
      "-webkit-appearance": "none",
      "-moz-appearance": "none",
    },
  },
});

export default withGlobalContext(withStyles(styles)(Filters));
